import React, { useState, useEffect } from 'react';
import {
  X,
  Share2,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  Globe,
  Lock,
  User,
  Mail,
  Phone,
  Activity,
  Users,
  BookOpen,
  Award,
  Calendar,
  PlayCircle,
  AtSign,
} from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { athleteSharingAPI, PrivacySettings, athleteAPI } from '../services/api';
import { integrationsAPI } from '../services/integrationsAPI';
import { useResponsive } from '../hooks/useResponsive';
import { useConfirm } from '../hooks/useConfirm';
import Modal from './Modal';
import { TextInput } from './ui';

interface ShareSettingsModalProps {
  athleteId: string;
  athleteName: string;
  // The athlete's saved handles, shown under the X and Instagram switch.
  xHandle?: string | null;
  instagramHandle?: string | null;
  onClose: () => void;
}

const ShareSettingsModal: React.FC<ShareSettingsModalProps> = ({
  athleteId,
  athleteName,
  xHandle,
  instagramHandle,
  onClose,
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<PrivacySettings | null>(null);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [profileHeadline, setProfileHeadline] = useState('');
  const [headlineChanged, setHeadlineChanged] = useState(false);
  const [viewCount, setViewCount] = useState(0);
  const [lastViewedAt, setLastViewedAt] = useState<string | null>(null);
  const [syncingYouTube, setSyncingYouTube] = useState(false);
  const [youtubeConnected, setYoutubeConnected] = useState(false);

  const { ConfirmComponent: RegenerateConfirm, confirm: confirmRegenerate } = useConfirm({
    title: 'Generate a new link?',
    message:
      "This breaks every link you've already shared. Anyone you sent the old link to will no longer be able to open your profile. This can't be undone.",
    confirmText: 'Generate new link',
    cancelText: 'Keep current link',
    variant: 'danger',
  });

  useEffect(() => {
    loadSettings();
  }, [athleteId]);

  const loadSettings = async () => {
    if (!sessionToken) return;

    try {
      setLoading(true);
      const data = await athleteSharingAPI.getShareSettings(sessionToken, athleteId);

      if (data.success) {
        setSettings(data.data.settings);
        setIsEnabled(data.data.isEnabled);
        setShareUrl(data.data.shareUrl || '');
        setProfileHeadline(data.data.settings?.profile_headline || '');
        setViewCount(data.data.viewCount || 0);
        setLastViewedAt(data.data.lastViewedAt || null);
      }
    } catch (error) {
      console.error('Failed to load share settings:', error);
      toast.error('Failed to load share settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableSharing = async () => {
    if (!sessionToken) return;

    try {
      setSaving(true);
      const data = await athleteSharingAPI.enableSharing(sessionToken, athleteId);

      if (data.success) {
        setSettings(data.data.settings);
        setShareUrl(data.data.shareUrl);
        setIsEnabled(true);
        toast.success('Profile sharing enabled!');
      } else {
        toast.error('Failed to enable sharing');
      }
    } catch (error) {
      console.error('Failed to enable sharing:', error);
      toast.error('Failed to enable sharing');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSettings = async (updates: Partial<PrivacySettings>) => {
    if (!sessionToken) return;

    try {
      const data = await athleteSharingAPI.updateSettings(sessionToken, athleteId, updates);

      if (data.success) {
        setSettings(data.data.settings);
        toast.success('Settings updated');
      } else {
        toast.error('Failed to update settings');
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    }
  };

  // Optimistic toggle with rollback. Flipping several switches must not spam a
  // toast per flip — we only surface a toast when a save actually fails.
  const handleToggleSetting = async (key: keyof PrivacySettings, value: boolean) => {
    if (!sessionToken || !settings) return;

    const previous = settings;
    setSettings((prev) => (prev ? ({ ...prev, [key]: value } as PrivacySettings) : prev));

    try {
      const data = await athleteSharingAPI.updateSettings(sessionToken, athleteId, { [key]: value });
      if (data.success) {
        setSettings(data.data.settings);
      } else {
        setSettings(previous);
        toast.error('Failed to update settings');
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
      setSettings(previous);
      toast.error('Failed to update settings');
    }
  };

  const handleRegenerateSlug = async () => {
    if (!sessionToken) return;

    const confirmed = await confirmRegenerate();
    if (!confirmed) return;

    try {
      setRegenerating(true);
      const data = await athleteSharingAPI.regenerateSlug(sessionToken, athleteId);

      if (data.success) {
        setSettings(data.data.settings);
        setShareUrl(data.data.shareUrl);
        toast.success('New share URL generated');
      } else {
        toast.error('Failed to regenerate URL');
      }
    } catch (error) {
      console.error('Failed to regenerate slug:', error);
      toast.error('Failed to regenerate URL');
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy share URL:', error);
      toast.error('Could not copy the link. Please copy it manually.');
    }
  };

  const handleOpenPreview = () => {
    if (!shareUrl) return;
    window.open(shareUrl, '_blank');
  };

  const [savingHeadline, setSavingHeadline] = useState(false);
  const handleSaveHeadline = async () => {
    setSavingHeadline(true);
    try {
      await handleUpdateSettings({ profile_headline: profileHeadline || null });
    } finally {
      setSavingHeadline(false);
    }
  };

  const handleSyncYouTube = async () => {
    if (!sessionToken) return;

    try {
      setSyncingYouTube(true);

      const channelData = await integrationsAPI.getYouTubeChannel(athleteId);

      if (channelData?.data?.id) {
        const youtubeUrl = `https://www.youtube.com/channel/${channelData.data.id}`;
        await athleteAPI.updateProfile(sessionToken, athleteId, {
          youtubeChannelUrl: youtubeUrl,
          youtubeChannelHandle: channelData.data.title || undefined
        });
        toast.success('YouTube channel synced!');
      } else {
        toast.error('YouTube channel not found');
      }
    } catch (error) {
      console.error('Failed to sync YouTube channel:', error);
      toast.error('Failed to sync YouTube channel. Please try again.');
    } finally {
      setSyncingYouTube(false);
    }
  };

  // Check YouTube connection status
  React.useEffect(() => {
    const loadConnectionStatus = async () => {
      if (!athleteId) return;

      try {
        const status = await integrationsAPI.getConnectionStatus(athleteId);
        setYoutubeConnected(status.youtube || false);
      } catch (error) {
        console.error('Failed to load connection status:', error);
        setYoutubeConnected(false);
      }
    };

    loadConnectionStatus();
  }, [athleteId]);

  const styles = {
    overlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '20px',
    },
    modal: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      maxWidth: isMobile ? 'calc(100vw - 32px)' : '600px',
      width: isMobile ? 'calc(100vw - 32px)' : '100%',
      maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column' as const,
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: isMobile ? '16px' : '24px',
      borderBottom: '1px solid #E5E7EB',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    iconCircle: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: '#f9550d',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#151133',
      margin: 0,
    },
    subtitle: {
      fontSize: '14px',
      color: '#6c757d',
      margin: '4px 0 0 0',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      padding: '8px',
      cursor: 'pointer',
      color: '#6c757d',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '6px',
      transition: 'all 0.2s ease',
    },
    content: {
      flex: 1,
      overflowY: 'auto' as const,
      padding: '24px',
    },
    section: {
      marginBottom: '24px',
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#151133',
      marginBottom: '12px',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    },
    enableCard: {
      backgroundColor: '#FFF7ED',
      border: '2px solid #FED7AA',
      borderRadius: '12px',
      padding: '24px',
      textAlign: 'center' as const,
    },
    enableTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#151133',
      marginBottom: '8px',
    },
    enableText: {
      fontSize: '14px',
      color: '#6c757d',
      marginBottom: '20px',
      lineHeight: '1.5',
    },
    enableButton: {
      backgroundColor: '#f9550d',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 32px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
    },
    urlCard: {
      backgroundColor: '#F9FAFB',
      border: '1px solid #E5E7EB',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '20px',
    },
    urlLabel: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#6c757d',
      marginBottom: '8px',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    },
    urlRow: {
      display: 'flex' as const,
      flexDirection: isMobile ? 'column' as const : 'row' as const,
      gap: '8px',
      marginBottom: '12px',
    },
    urlInput: {
      flex: isMobile ? 'none' : 1,
      width: isMobile ? '100%' : 'auto',
      padding: '10px 14px',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#151133',
      backgroundColor: '#ffffff',
      fontFamily: 'monospace',
      boxSizing: 'border-box' as const,
    },
    urlButton: {
      padding: '10px 16px',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: isMobile ? 'center' : 'flex-start',
      gap: '6px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#151133',
      transition: 'all 0.2s ease',
      width: isMobile ? '100%' : 'auto',
    },
    urlButtonPrimary: {
      backgroundColor: '#f9550d',
      color: '#ffffff',
      border: 'none',
    },
    statsRow: {
      display: 'flex',
      gap: '16px',
    },
    statBox: {
      flex: 1,
      backgroundColor: '#ffffff',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      padding: '12px',
      textAlign: 'center' as const,
    },
    statValue: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#f9550d',
      marginBottom: '4px',
    },
    statLabel: {
      fontSize: '12px',
      color: '#6c757d',
      fontWeight: '500',
    },
    headlineInput: {
      width: '100%',
      padding: '12px 14px',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#151133',
      marginBottom: '8px',
      fontFamily: 'Manrope, sans-serif',
      boxSizing: 'border-box' as const,
    },
    headlineHint: {
      fontSize: '12px',
      color: '#6c757d',
      marginBottom: '8px',
    },
    saveButton: {
      padding: '8px 16px',
      backgroundColor: '#f9550d',
      color: '#ffffff',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    toggleList: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px',
    },
    toggleItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '14px 16px',
      backgroundColor: '#F9FAFB',
      border: '1px solid #E5E7EB',
      borderRadius: '10px',
      transition: 'all 0.2s ease',
    },
    toggleItemActive: {
      backgroundColor: '#FFF7ED',
      borderColor: '#f9550d',
    },
    toggleLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    toggleIcon: {
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      backgroundColor: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#f9550d',
    },
    toggleContent: {
      flex: 1,
    },
    toggleLabel: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#151133',
      marginBottom: '2px',
    },
    toggleDescription: {
      fontSize: '13px',
      color: '#6c757d',
    },
    // The X and Instagram switch sits indented under Social Media Links.
    nestedToggleWrap: {
      marginLeft: isMobile ? '12px' : '20px',
      paddingLeft: isMobile ? '10px' : '12px',
      borderLeft: '2px solid var(--aps-border-strong)',
    },
    nestedToggleItem: {
      alignItems: 'flex-start' as const,
      gap: '12px',
    },
    switch: {
      position: 'relative' as const,
      width: '48px',
      height: '28px',
      backgroundColor: '#cbd5e0',
      borderRadius: '14px',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
    },
    switchActive: {
      backgroundColor: '#f9550d',
    },
    switchKnob: {
      position: 'absolute' as const,
      top: '3px',
      left: '3px',
      width: '22px',
      height: '22px',
      backgroundColor: '#ffffff',
      borderRadius: '50%',
      transition: 'transform 0.2s ease',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },
    switchKnobActive: {
      transform: 'translateX(20px)',
    },
    divider: {
      height: '1px',
      backgroundColor: '#E5E7EB',
      margin: '24px 0',
    },
    loadingContainer: {
      padding: '60px 20px',
      textAlign: 'center' as const,
    },
    loadingText: {
      fontSize: '14px',
      color: '#6c757d',
      marginTop: '12px',
    },
    sectionDescription: {
      fontSize: '14px',
      color: '#6c757d',
      marginBottom: '16px',
      lineHeight: '1.5',
    },
    syncButton: {
      backgroundColor: '#f9550d',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
    },
    syncButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  };

  const privacyToggles = [
    {
      key: 'share_personal_info' as keyof PrivacySettings,
      icon: <User size={20} />,
      label: 'Personal Info',
      description: 'City, state, location details',
    },
    {
      key: 'share_contact_info' as keyof PrivacySettings,
      icon: <Mail size={20} />,
      label: 'Contact Info',
      description: 'Email and phone number',
    },
    {
      key: 'share_athletic_info' as keyof PrivacySettings,
      icon: <Activity size={20} />,
      label: 'Athletic Info',
      description: 'Position, height, weight, bats/throws',
    },
    {
      key: 'share_performance_metrics' as keyof PrivacySettings,
      icon: <Activity size={20} />,
      label: 'Performance Metrics',
      description: 'Measurables and performance data',
    },
    {
      key: 'share_team_info' as keyof PrivacySettings,
      icon: <Users size={20} />,
      label: 'Team Info',
      description: 'School and club team information',
    },
    {
      key: 'share_academic_info' as keyof PrivacySettings,
      icon: <BookOpen size={20} />,
      label: 'Academic Info',
      description: 'GPA, test scores, academic performance',
    },
    {
      key: 'share_awards' as keyof PrivacySettings,
      icon: <Award size={20} />,
      label: 'Awards & Achievements',
      description: 'Awards, honors, and recognitions',
    },
    {
      key: 'share_tournament_schedule' as keyof PrivacySettings,
      icon: <Calendar size={20} />,
      label: 'Tournament Schedule',
      description: 'Upcoming games and tournaments',
    },
    {
      key: 'share_past_tournaments' as keyof PrivacySettings,
      icon: <Calendar size={20} />,
      label: 'Past Tournaments',
      description: 'Include completed tournaments',
    },
    {
      key: 'share_social_links' as keyof PrivacySettings,
      icon: <PlayCircle size={20} />,
      label: 'Social Media Links',
      description: 'YouTube channel and social profiles',
    },
  ];

  /*
   * The X and Instagram switch (share_social_handles, migration 049). The public
   * page shows those links only when this and Social Media Links are both on.
   * Shown only when the server returns the key, so an API without the column
   * shows nothing new. It can always be turned off, but only turned on while
   * Social Media Links is on.
   */
  const handlesSwitchAvailable = settings ? 'share_social_handles' in settings : false;
  const socialLinksOn = !!settings?.share_social_links;
  const handlesOn = !!settings?.share_social_handles;
  const handlesSwitchDisabled = !socialLinksOn && !handlesOn;
  const xOnFile = (xHandle || '').trim().replace(/^@+/, '');
  const instagramOnFile = (instagramHandle || '').trim().replace(/^@+/, '');
  const handlesOnFile = [
    xOnFile && `@${xOnFile} on X`,
    instagramOnFile && `@${instagramOnFile} on Instagram`,
  ]
    .filter(Boolean)
    .join(', ');
  const handlesHint = socialLinksOn ? handlesOnFile : 'Turn on Social Media Links first.';
  const handlesLabel = 'Show my X and Instagram on my public profile';

  const socialHandlesRow = handlesSwitchAvailable ? (
    <div style={styles.nestedToggleWrap}>
      <div
        style={{
          ...styles.toggleItem,
          ...styles.nestedToggleItem,
          ...(handlesOn && socialLinksOn ? styles.toggleItemActive : {}),
        }}
      >
        <div style={{ ...styles.toggleLeft, alignItems: 'flex-start' }}>
          <div style={{ ...styles.toggleIcon, flexShrink: 0 }}>
            <AtSign size={20} />
          </div>
          <div style={styles.toggleContent}>
            <div style={styles.toggleLabel}>{handlesLabel}</div>
            {handlesHint && (
              <div id="share-social-handles-hint" style={styles.toggleDescription}>
                {handlesHint}
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={handlesOn}
          aria-label={handlesLabel}
          aria-describedby={handlesHint ? 'share-social-handles-hint' : undefined}
          disabled={handlesSwitchDisabled}
          style={{
            ...styles.switch,
            ...(handlesOn ? styles.switchActive : {}),
            border: 'none',
            padding: 0,
            flexShrink: 0,
            marginTop: '2px',
            cursor: handlesSwitchDisabled ? 'not-allowed' : 'pointer',
            opacity: socialLinksOn ? 1 : 0.55,
          }}
          onClick={() => handleToggleSetting('share_social_handles', !handlesOn)}
        >
          <div
            style={{
              ...styles.switchKnob,
              ...(handlesOn ? styles.switchKnobActive : {}),
            }}
          />
        </button>
      </div>
    </div>
  ) : null;

  if (loading) {
    return (
      <Modal
        isOpen
        onClose={onClose}
        ariaLabel="Share settings"
        contentStyle={{ ...styles.modal, overflowY: 'hidden' }}
        overlayStyle={{ padding: isMobile ? '16px' : '20px' }}
      >
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.iconCircle}>
              <Share2 size={20} />
            </div>
            <div>
              <h2 style={styles.title}>Share Settings</h2>
              <p style={styles.subtitle}>{athleteName}</p>
            </div>
          </div>
          <button style={styles.closeButton} onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        <div style={styles.loadingContainer}>
          <RefreshCw size={32} color="#f9550d" style={{ animation: 'spin 1s linear infinite' }} />
          <p style={styles.loadingText}>Loading share settings...</p>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <RegenerateConfirm />
      <Modal
        isOpen
        onClose={onClose}
        ariaLabel="Share settings"
        isDirty={profileHeadline !== (settings?.profile_headline || '')}
        contentStyle={{ ...styles.modal, overflowY: 'hidden' }}
        overlayStyle={{ padding: isMobile ? '16px' : '20px' }}
      >
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.iconCircle}>
              <Share2 size={20} />
            </div>
            <div>
              <h2 style={styles.title}>Share Settings</h2>
              <p style={styles.subtitle}>{athleteName}</p>
            </div>
          </div>
          <button
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X size={24} />
          </button>
        </div>

        <div style={styles.content}>
          {!isEnabled ? (
            <div style={styles.enableCard}>
              <Globe size={48} color="#f9550d" style={{ marginBottom: '16px' }} />
              <h3 style={styles.enableTitle}>Enable Profile Sharing</h3>
              <p style={styles.enableText}>
                Share your athlete profile with coaches and recruiters. You'll get a unique link that you can share via email, text, or social media.
              </p>
              <button
                style={styles.enableButton}
                onClick={handleEnableSharing}
                disabled={saving}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e64a0c')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f9550d')}
              >
                {saving ? (
                  <>
                    <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Enabling...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    Enable Sharing
                  </>
                )}
              </button>
            </div>
          ) : (
            <>
              {/* Share URL Section */}
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Public Profile Link</h3>
                <div style={styles.urlCard}>
                  <p style={styles.urlLabel}>Your Share URL</p>
                  <div style={styles.urlRow}>
                    <TextInput
                      type="text"
                      value={shareUrl}
                      readOnly
                      aria-label="Your share URL"
                      style={styles.urlInput}
                    />
                    <button
                      style={{ ...styles.urlButton, ...(copied ? styles.urlButtonPrimary : {}) }}
                      onClick={handleCopyUrl}
                      onMouseEnter={(e) => !copied && (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                      onMouseLeave={(e) => !copied && (e.currentTarget.style.backgroundColor = '#ffffff')}
                    >
                      {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                      style={styles.urlButton}
                      onClick={handleOpenPreview}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                    >
                      <ExternalLink size={16} />
                      Preview
                    </button>
                  </div>
                  <div style={styles.statsRow}>
                    <div style={styles.statBox}>
                      <div style={styles.statValue}>{viewCount}</div>
                      <div style={styles.statLabel}>Profile Views</div>
                    </div>
                    <div style={styles.statBox}>
                      <div style={styles.statValue}>
                        {lastViewedAt
                          ? new Date(lastViewedAt).toLocaleDateString()
                          : 'Never'}
                      </div>
                      <div style={styles.statLabel}>Last Viewed</div>
                    </div>
                  </div>
                </div>
                <button
                  style={{
                    ...styles.urlButton,
                    width: '100%',
                    justifyContent: 'center',
                  }}
                  onClick={handleRegenerateSlug}
                  disabled={regenerating}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                >
                  {regenerating ? (
                    <>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={16} />
                      Generate New URL
                    </>
                  )}
                </button>
              </div>

              {/* Profile Headline */}
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Profile Headline</h3>
                <TextInput
                  type="text"
                  value={profileHeadline}
                  onChange={(e) => setProfileHeadline(e.target.value)}
                  placeholder="e.g., RHP | 6'2&quot; | 185 lbs | 3.8 GPA"
                  aria-label="Profile headline"
                  style={styles.headlineInput}
                  maxLength={200}
                />
                <p style={styles.headlineHint}>
                  Add a custom tagline to your public profile (optional)
                </p>
                {profileHeadline !== (settings?.profile_headline || '') && (
                  <button style={{ ...styles.saveButton, opacity: savingHeadline ? 0.6 : 1, cursor: savingHeadline ? 'not-allowed' : 'pointer' }} onClick={handleSaveHeadline} disabled={savingHeadline}>
                    {savingHeadline ? 'Saving…' : 'Save Headline'}
                  </button>
                )}
              </div>

              <div style={styles.divider} />

              {/* Privacy Controls */}
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>What to Share</h3>
                <div style={styles.toggleList}>
                  {privacyToggles.map((toggle) => {
                    const isActive = settings?.[toggle.key] as boolean;
                    return (
                      <React.Fragment key={toggle.key}>
                        <div
                          style={{
                            ...styles.toggleItem,
                            ...(isActive ? styles.toggleItemActive : {}),
                          }}
                        >
                          <div style={styles.toggleLeft}>
                            <div style={styles.toggleIcon}>{toggle.icon}</div>
                            <div style={styles.toggleContent}>
                              <div style={styles.toggleLabel}>{toggle.label}</div>
                              <div style={styles.toggleDescription}>{toggle.description}</div>
                            </div>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={isActive}
                            aria-label={toggle.label}
                            style={{
                              ...styles.switch,
                              ...(isActive ? styles.switchActive : {}),
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                            }}
                            onClick={() => handleToggleSetting(toggle.key, !isActive)}
                          >
                            <div
                              style={{
                                ...styles.switchKnob,
                                ...(isActive ? styles.switchKnobActive : {}),
                              }}
                            />
                          </button>
                        </div>
                        {toggle.key === 'share_social_links' && socialHandlesRow}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              <div style={styles.divider} />

              {/* YouTube Sync Button */}
              {youtubeConnected && (
                <>
                  <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Social Media</h3>
                    <p style={styles.sectionDescription}>
                      Keep your YouTube channel information up to date
                    </p>
                    <button
                      style={{
                        ...styles.syncButton,
                        ...(syncingYouTube ? styles.syncButtonDisabled : {}),
                      }}
                      onClick={handleSyncYouTube}
                      disabled={syncingYouTube}
                    >
                      <PlayCircle size={18} />
                      {syncingYouTube ? 'Syncing...' : 'Sync YouTube Channel'}
                    </button>
                  </div>
                  <div style={styles.divider} />
                </>
              )}

              {/* Master Toggle */}
              <div style={styles.section}>
                <div
                  style={{
                    ...styles.toggleItem,
                    ...(settings?.share_enabled ? styles.toggleItemActive : {}),
                  }}
                >
                  <div style={styles.toggleLeft}>
                    <div style={styles.toggleIcon}>
                      {settings?.share_enabled ? <Eye size={20} /> : <EyeOff size={20} />}
                    </div>
                    <div style={styles.toggleContent}>
                      <div style={styles.toggleLabel}>Profile Sharing</div>
                      <div style={styles.toggleDescription}>
                        {settings?.share_enabled
                          ? 'Your profile is publicly accessible'
                          : 'Your profile is private'}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!settings?.share_enabled}
                    aria-label="Profile sharing"
                    style={{
                      ...styles.switch,
                      ...(settings?.share_enabled ? styles.switchActive : {}),
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      const next = !settings?.share_enabled;
                      handleToggleSetting('share_enabled', next);
                      // Keep the top-level enabled/disabled view (keyed off isEnabled)
                      // in sync — otherwise turning sharing off here leaves the URL/
                      // stats section showing as if still enabled.
                      setIsEnabled(next);
                    }}
                  >
                    <div
                      style={{
                        ...styles.switchKnob,
                        ...(settings?.share_enabled ? styles.switchKnobActive : {}),
                      }}
                    />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

export default ShareSettingsModal;
